// src/context/UploadManagerContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import axios from "axios";

const UploadManagerContext = createContext(null);

export const useUploadManager = () => {
  const ctx = useContext(UploadManagerContext);
  if (!ctx) {
    throw new Error("useUploadManager must be used inside <UploadManagerProvider>");
  }
  return ctx;
};

// ---- Tuning ----
const CHUNK_SIZE = 16 * 1024 * 1024; // 16 MB per chunk (fewer requests = faster speed)
const PARALLEL_CHUNKS = 6;
const MAX_RETRIES = 3;
// Larger files cannot be stored as blobs in memory, so use the native browser download instead.
const MAX_BLOB_DOWNLOAD = 700 * 1024 * 1024;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const triggerBrowserDownload = (href, fileName) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => document.body.removeChild(a), 100);
};

export const UploadManagerProvider = ({ children }) => {
  // ---------------- UPLOAD STATE ----------------
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("upload");
  const [startedAt, setStartedAt] = useState(0);
  const [finishedAt, setFinishedAt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [batchCaseId, setBatchCaseId] = useState(null);

  const abortRef = useRef(null);
  const lastProgressRef = useRef({});
  const busyRef = useRef(false);
  const closeTimerRef = useRef(null);

  // ---------------- DOWNLOAD STATE ----------------
  const [downloads, setDownloads] = useState([]);
  const dlControllers = useRef({});
  const dlCloseTimer = useRef(null);
    // ---------------- DELETE STATE ----------------
  const [deletes, setDeletes] = useState([]);
  const delCloseTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      Object.values(dlControllers.current).forEach((c) => c.abort());
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (dlCloseTimer.current) clearTimeout(dlCloseTimer.current);
       if (delCloseTimer.current) clearTimeout(delCloseTimer.current); // NEW
    };
  }, []);

  const patchItem = useCallback((id, patch) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  }, []);

  const getErrorMessage = useCallback((err) => {
    if (err.response) {
      if (err.response.data?.message) return err.response.data.message;
      return `Server error (${err.response.status})`;
    }
    return err.message || "Upload failed";
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  // =====================================================
  // UPLOAD (pehle jaisa)
  // =====================================================
  const startBatch = useCallback(
    async (files, kindArg, buildRequest /* , concurrency (ignored) */) => {
      if (busyRef.current) {
        return { done: 0, failed: 0, canceled: 0, lastError: "Busy" };
      }

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      busyRef.current = true;
      setBusy(true);

      const controller = new AbortController();
      abortRef.current = controller;
      lastProgressRef.current = {};

      const stamp = Date.now();
      const newItems = files.map((file, idx) => ({
        id: `${stamp}-${idx}`,
        name: file.name,
        size: file.size,
        loaded: 0,
        status: "queued",
        error: "",
      }));

      setItems(newItems);
      setKind(kindArg);
      setStartedAt(stamp);
      setFinishedAt(0);

      const infos = files.map((f) => buildRequest(f));
      setBatchCaseId(infos[0]?.caseId ?? null);

      const results = { done: 0, failed: 0, canceled: 0, lastError: "" };

      const state = files.map((f) => ({
        total: Math.max(1, Math.ceil(f.size / CHUNK_SIZE)),
        doneChunks: 0,
        uploadId: null,
        final: null,
        chunkLoaded: {},
      }));

      const apiBase = (i) => `${infos[i].baseUrl}/api/chunk-upload`;
      const baseHeaders = (i) => infos[i].headers || {};

      const markError = (i, err) => {
        if (state[i].final) return;
        const message = typeof err === "string" ? err : getErrorMessage(err);
        state[i].final = "error";
        results.failed += 1;
        results.lastError = message;
        patchItem(newItems[i].id, { status: "error", error: message });
        if (state[i].uploadId) {
          axios
            .delete(`${apiBase(i)}/${state[i].uploadId}`, { withCredentials: true })
            .catch(() => {});
        }
      };

      const reportProgress = (i) => {
        const id = newItems[i].id;
        const now = Date.now();
        const last = lastProgressRef.current[id] || 0;
        const loaded = Object.values(state[i].chunkLoaded).reduce((a, b) => a + b, 0);
        const complete = loaded >= files[i].size;
        if (!complete && now - last < 100) return;
        lastProgressRef.current[id] = now;
        patchItem(id, { loaded: Math.min(loaded, files[i].size) });
      };

      // 1. INIT
      await Promise.all(
        files.map(async (file, i) => {
          try {
            const { data } = await axios.post(
              `${apiBase(i)}/init`,
              {
                fileName: file.name,
                size: file.size,
                chunkSize: CHUNK_SIZE,
                mimetype: file.type || "application/octet-stream",
              },
              {
                withCredentials: true,
                headers: baseHeaders(i),
                signal: controller.signal,
              }
            );
            if (!data.success) throw new Error(data.message || "Init failed");
            state[i].uploadId = data.uploadId;
          } catch (err) {
            if (axios.isCancel(err) || controller.signal.aborted) return;
            markError(i, err);
          }
        })
      );

      // 2. Chunk queue
      const queue = [];
      files.forEach((_, i) => {
        if (!state[i].uploadId) return;
        for (let c = 0; c < state[i].total; c++) queue.push({ i, index: c });
      });

      const completions = [];

      const sendChunk = async (i, index) => {
        const file = files[i];
        const start = index * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const len = end - start;

        for (let attempt = 1; ; attempt++) {
          try {
            await axios.put(`${apiBase(i)}/chunk/${state[i].uploadId}`, blob, {
              params: { index },
              withCredentials: true,
              timeout: 0,
              signal: controller.signal,
              headers: {
                "Content-Type": "application/octet-stream",
                ...baseHeaders(i),
              },
              onUploadProgress: (e) => {
                state[i].chunkLoaded[index] = Math.min(e.loaded, len);
                reportProgress(i);
              },
            });
            state[i].chunkLoaded[index] = len;
            reportProgress(i);
            return;
          } catch (err) {
            if (axios.isCancel(err) || controller.signal.aborted) throw err;
            if (attempt >= MAX_RETRIES) throw err;
            state[i].chunkLoaded[index] = 0;
            await sleep(500 * attempt);
          }
        }
      };

      const finishFile = async (i) => {
        try {
          const { data } = await axios.post(
            `${apiBase(i)}/complete/${state[i].uploadId}`,
            {
              caseId: infos[i].caseId,
              mode: infos[i].mode || "upload",
              fileId: infos[i].fileId,
            },
            {
              withCredentials: true,
              headers: baseHeaders(i),
              signal: controller.signal,
              timeout: 0,
            }
          );
          if (!data.success) throw new Error(data.message || "Save failed");
          state[i].final = "done";
          results.done += 1;
          patchItem(newItems[i].id, { status: "done", loaded: files[i].size });
        } catch (err) {
          if (axios.isCancel(err) || controller.signal.aborted) return;
          markError(i, err);
        }
      };

      const worker = async () => {
        while (!controller.signal.aborted) {
          const task = queue.shift();
          if (!task) return;
          const { i, index } = task;
          if (state[i].final === "error") continue;

          if (index === 0 || newItems[i].status === "queued") {
            patchItem(newItems[i].id, { status: "uploading" });
          }

          try {
            await sendChunk(i, index);
            state[i].doneChunks += 1;
            if (state[i].doneChunks === state[i].total) {
              completions.push(finishFile(i));
            }
          } catch (err) {
            if (axios.isCancel(err) || controller.signal.aborted) return;
            markError(i, err);
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(PARALLEL_CHUNKS, Math.max(queue.length, 1)) }, worker)
      );
      await Promise.all(completions);

      // 3. Unfinished = canceled
      files.forEach((_, i) => {
        if (state[i].final) return;
        results.canceled += 1;
        patchItem(newItems[i].id, { status: "canceled" });
        if (state[i].uploadId) {
          axios
            .delete(`${apiBase(i)}/${state[i].uploadId}`, { withCredentials: true })
            .catch(() => {});
        }
      });

      abortRef.current = null;
      busyRef.current = false;
      setBusy(false);
      setFinishedAt(Date.now());

      if (results.failed === 0 && results.canceled === 0) {
        closeTimerRef.current = setTimeout(() => {
          setItems([]);
          setBatchCaseId(null);
          closeTimerRef.current = null;
        }, 3000);
      }

      return results;
    },
    [patchItem, getErrorMessage]
  );

  const dismiss = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setItems([]);
    setBatchCaseId(null);
  }, []);

  // =====================================================
  // DOWNLOAD (NEW) - streaming + progress + time
  // =====================================================
  const patchDownload = useCallback((id, patch) => {
    setDownloads((prev) =>
      prev.map((d) => (d.id === id ? { ...d, ...patch } : d))
    );
  }, []);

  const scheduleDownloadsAutoClear = useCallback(() => {
    if (dlCloseTimer.current) clearTimeout(dlCloseTimer.current);
    dlCloseTimer.current = setTimeout(() => {
      setDownloads((prev) => {
        const allOk = prev.every((d) => d.status === "done");
        return allOk ? [] : prev;
      });
      dlCloseTimer.current = null;
    }, 5000);
  }, []);

  /**
   * @param {{url: string, fileName: string, size?: number}} opts
   */
  const startDownload = useCallback(
    async ({ url, fileName, size = 0 }) => {
      if (dlCloseTimer.current) {
        clearTimeout(dlCloseTimer.current);
        dlCloseTimer.current = null;
      }

      const id = `dl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const controller = new AbortController();
      dlControllers.current[id] = controller;
      const t0 = Date.now();

      setDownloads((prev) => [
        ...prev,
        {
          id,
          name: fileName,
          size: size || 0,
          loaded: 0,
          status: "downloading",
          error: "",
          startedAt: t0,
          finishedAt: 0,
        },
      ]);

      try {
        const res = await fetch(url, {
          credentials: "include",
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Server error (${res.status})`);

// NOTE: In cross-origin requests, the Content-Length header is only accessible
// if the server sends Access-Control-Expose-Headers: Content-Length.
        const headerLen = Number(res.headers.get("content-length")) || 0;
        const total = headerLen || size || 0;
        patchDownload(id, { size: total });

        // For very large files, avoid creating a blob in memory and use the browser's native download instead.
        if (total > MAX_BLOB_DOWNLOAD) {
          controller.abort();
          triggerBrowserDownload(url, fileName);
          patchDownload(id, {
            status: "done",
            loaded: total,
            finishedAt: Date.now(),
          });
          return { ok: true, native: true };
        }

        const reader = res.body.getReader();
        const chunks = [];
        let loaded = 0;
        let lastPaint = 0;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.length;
          const now = Date.now();
          if (now - lastPaint > 100) {
            lastPaint = now;
            patchDownload(id, { loaded });
          }
        }

        const blob = new Blob(chunks, {
          type: res.headers.get("content-type") || "application/octet-stream",
        });
        const blobUrl = URL.createObjectURL(blob);
        triggerBrowserDownload(blobUrl, fileName);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);

        patchDownload(id, {
          status: "done",
          loaded,
          size: total || loaded,
          finishedAt: Date.now(),
        });
        return { ok: true };
      } catch (err) {
        if (controller.signal.aborted && err.name === "AbortError") {
          patchDownload(id, { status: "canceled", finishedAt: Date.now() });
          return { ok: false, canceled: true };
        }
        patchDownload(id, {
          status: "error",
          error: err.message || "Download failed",
          finishedAt: Date.now(),
        });
        return { ok: false, error: err.message };
      } finally {
        delete dlControllers.current[id];
        scheduleDownloadsAutoClear();
      }
    },
    [patchDownload, scheduleDownloadsAutoClear]
  );

  const cancelDownloads = useCallback(() => {
    Object.values(dlControllers.current).forEach((c) => c.abort());
  }, []);

  const dismissDownloads = useCallback(() => {
    if (dlCloseTimer.current) {
      clearTimeout(dlCloseTimer.current);
      dlCloseTimer.current = null;
    }
    setDownloads((prev) => prev.filter((d) => d.status === "downloading"));
  }, []);
    // =====================================================
  // DELETE (NEW) - progress + time
  // =====================================================
  const patchDelete = useCallback((id, patch) => {
    setDeletes((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  }, []);

  const scheduleDeletesAutoClear = useCallback(() => {
    if (delCloseTimer.current) clearTimeout(delCloseTimer.current);
    delCloseTimer.current = setTimeout(() => {
      setDeletes((prev) => {
        const busyOrFailed = prev.some((d) => d.status !== "done");
        return busyOrFailed ? prev : [];
      });
      delCloseTimer.current = null;
    }, 4000);
  }, []);

  /**
   * @param {{url: string, fileName: string, size?: number}} opts
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  const startDelete = useCallback(
    async ({ url, fileName, size = 0 }) => {
      if (delCloseTimer.current) {
        clearTimeout(delCloseTimer.current);
        delCloseTimer.current = null;
      }

      const id = `del-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      setDeletes((prev) => [
        ...prev,
        {
          id,
          name: fileName,
          size: size || 0,
          loaded: 0,
          status: "deleting",
          error: "",
          startedAt: Date.now(),
          finishedAt: 0,
        },
      ]);

      try {
                const t0 = Date.now();
        await axios.delete(url, { withCredentials: true, timeout: 0 });
        // tez delete mein bhi panel nazar aaye
        const wait = 1000 - (Date.now() - t0);
        if (wait > 0) await sleep(wait);
        patchDelete(id, {
          status: "done",
          loaded: size || 0,
          finishedAt: Date.now(),
        });
        return { ok: true };
      } catch (err) {
        const message = getErrorMessage(err);
        patchDelete(id, {
          status: "error",
          error: message,
          finishedAt: Date.now(),
        });
        return { ok: false, error: message };
      } finally {
        scheduleDeletesAutoClear();
      }
    },
    [patchDelete, scheduleDeletesAutoClear, getErrorMessage]
  );

  const dismissDeletes = useCallback(() => {
    if (delCloseTimer.current) {
      clearTimeout(delCloseTimer.current);
      delCloseTimer.current = null;
    }
    setDeletes((prev) => prev.filter((d) => d.status === "deleting"));
  }, []);

  const value = {
    // upload
    items,
    kind,
    startedAt,
    finishedAt,
    batchCaseId,
    busy,
    startBatch,
    cancel,
    dismiss,
    isUploading: items.some(
      (i) => i.status === "queued" || i.status === "uploading"
    ),
    // download
    downloads,
    startDownload,
    cancelDownloads,
    dismissDownloads,
    isDownloading: downloads.some((d) => d.status === "downloading"),
       // delete  <-- ADD THIS
    deletes,
    startDelete,
    dismissDeletes,
    isDeleting: deletes.some((d) => d.status === "deleting"),
  };

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
};